import React, { useMemo } from 'react';
import { ComparableItem, HistoryHebrewItem } from 'store/state/selectors/insights/prices';
import { ColumnRendererProps, filterItemData } from '../../../../utils';
import { AgentPhoto, AgentHoverPhoto, OfficeLogosRoot } from './styles';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { TableColumn } from 'ds/components/table';
import { State } from 'store/state';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { TextLabel, SmallText } from 'ds/components/typography';
import { IGetPublicRealEstateAgentsByIdsResponseItem, IGetPublicRealEstateOfficesByIdsResponseItem } from 'store/sagas/apiService/types';
import { safeDealsAgentsByIdsSelector, safeDealsOfficesByIdsSelector } from 'store/state/domainData/selectors';
import { useLocale } from 'locale';

export const getHistoryAgentColumnConfig = (): Partial<TableColumn<ComparableItem>> => ({
  width: '78px',
  gap: 0,
  align: 'right',
});

const LabelContainer = styled.div(({ theme }) => ({
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '22px',
  padding: theme.spacing(0.5, 0.75),
  gap: theme.spacing(0.5),
  backgroundColor: theme.colors.brand.mocha,
  borderRadius: theme.borderRadius.tiny,
  border: `1px solid ${theme.colors.neutrals.grey8}`,
  boxSizing: 'border-box',
}));

const StyledLabelText = styled(TextLabel)(({ theme }) => ({
  color: theme.colors.neutrals.grey1,
}));

const BadgeContainer = styled.div(({ theme }) => ({
  width: '24px',
  height: '24px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.colors.neutrals.grey10,
  border: `1px solid ${theme.colors.neutrals.grey8}`,
}));

const StyledBadgeText = styled(SmallText)(({ theme }) => ({
  color: theme.colors.neutrals.grey1,
}));

export const CounterBadge: React.FC = () => {
  const { t } = useLocale();
  return (
    <BadgeContainer>
      <StyledBadgeText>
        {t('widget.prices.agentsolo.hidden.title')}
      </StyledBadgeText>
    </BadgeContainer>
  );
};

export const TransactionLabel: React.FC = () => {
  const { t } = useLocale();
  return (
    <LabelContainer>
      <StyledLabelText weight="bold" small>
        {t('widget.prices.agentgroup.hidden.title')}
      </StyledLabelText>
    </LabelContainer>
  );
};

interface HistoryAgentColumnProps extends ColumnRendererProps<HistoryHebrewItem> {
  dealsOfficesData?: IGetPublicRealEstateOfficesByIdsResponseItem[];
  dealsAgentsData?: IGetPublicRealEstateAgentsByIdsResponseItem[];
}

const HistoryAgentColumn: React.FC<HistoryAgentColumnProps> = ({ item, dealsOfficesData, dealsAgentsData }) => {
  if (!item.seller && !item.buyer) return null;

  const constructImageURI = useImageUrlBuilder();

  const buyerAgent = useMemo(() => filterItemData(item.buyer, dealsAgentsData, dealsOfficesData, true), [ item.buyer, dealsOfficesData, dealsAgentsData ]);
  const sellerAgent = useMemo(() => filterItemData(item.seller, dealsAgentsData, dealsOfficesData, true), [ item.seller, dealsOfficesData, dealsAgentsData ]);
  const isSameAgent = buyerAgent && sellerAgent && buyerAgent.agentId === sellerAgent.agentId && buyerAgent.agencyId === sellerAgent.agencyId;

  const oneAgent = buyerAgent || sellerAgent;

  if (buyerAgent && sellerAgent) {

    if (buyerAgent.hideDealsOnPublicSite && sellerAgent.hideDealsOnPublicSite) return <TransactionLabel />;

    if (buyerAgent.hideDealsOnPublicSite) {
      return (
        <OfficeLogosRoot>
          <AgentPhoto data-auto={`agency-id-${sellerAgent.agencyId}`} imageURL={constructImageURI(sellerAgent.agencyImageUrl)}>
            <AgentHoverPhoto imageURL={constructImageURI(sellerAgent.agencyImageUrl)} />
          </AgentPhoto>
          <CounterBadge />
        </OfficeLogosRoot>
      );
    }

    if (sellerAgent.hideDealsOnPublicSite) {
      return (
        <OfficeLogosRoot>
          <AgentPhoto data-auto={`agency-id-${buyerAgent.agencyId}`} imageURL={constructImageURI(buyerAgent.agencyImageUrl)}>
            <AgentHoverPhoto imageURL={constructImageURI(buyerAgent.agencyImageUrl)} />
          </AgentPhoto>
          <CounterBadge />
        </OfficeLogosRoot>
      );
    }
  }

  if (oneAgent) {
    if (oneAgent.hideDealsOnPublicSite) return <TransactionLabel />;
  }

  return (
    <OfficeLogosRoot>
      {buyerAgent ? (
        <AgentPhoto data-auto={`agency-id-${buyerAgent.agencyId}`} imageURL={constructImageURI(buyerAgent.agencyImageUrl)}>
          <AgentHoverPhoto imageURL={constructImageURI(buyerAgent.agencyImageUrl)} />
        </AgentPhoto>
      ) : null}
      {sellerAgent && !isSameAgent ? (
        <AgentPhoto data-auto={`agency-id-${sellerAgent.agencyId}`} imageURL={constructImageURI(sellerAgent.agencyImageUrl)}>
          <AgentHoverPhoto imageURL={constructImageURI(sellerAgent.agencyImageUrl)} />
        </AgentPhoto>
      ) : null}
    </OfficeLogosRoot>
  );
};

const mapStateToProps = (state: State) => ({
  dealsOfficesData: safeDealsOfficesByIdsSelector(state),
  dealsAgentsData: safeDealsAgentsByIdsSelector(state),
});

export default connect(mapStateToProps)(HistoryAgentColumn);
